import { graphql, useStaticQuery } from "gatsby"
import React, { useContext } from "react"
import { css } from "styled-components"
import BreakpointImage from "../../design-system/atoms/breakpoint-image"
import Button from "../../design-system/atoms/button"
import { Grid } from "../../design-system/atoms/layout-primitives"
import { T } from "../../design-system/atoms/text"
import VariantContainer from "../../design-system/atoms/variant-container"
import { IFluidImageQuery } from "../../design-system/types/imageQuery.types"
import Breakpoints from "../shared/breakpoints"
import { UIContext } from "../shared/layout"

interface IPrebuiltApps {
  description: string
  buttonText: string
  buttonHref: string
}

interface IPrebuiltAppsStaticQuery {
  mobile: IFluidImageQuery
  tablet: IFluidImageQuery
  desktop: IFluidImageQuery
}

export default function PrebuiltApps({
  description,
  buttonText,
  buttonHref,
}: IPrebuiltApps) {
  const { mobile, tablet, desktop } =
    useStaticQuery<IPrebuiltAppsStaticQuery>(graphql`
      query {
        desktop: file(
          relativePath: {
            eq: "1_SOLUTIONS/Developer Tools/eve tom desktop.jpg"
          }
        ) {
          childImageSharp {
            fluid(maxWidth: 1440, quality: 90) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
        tablet: file(
          relativePath: { eq: "1_SOLUTIONS/Developer Tools/eve tom tablet.jpg" }
        ) {
          childImageSharp {
            fluid(maxWidth: 1440, quality: 90) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
        mobile: file(
          relativePath: { eq: "1_SOLUTIONS/Developer Tools/eve tom mobile.jpg" }
        ) {
          childImageSharp {
            fluid(maxWidth: 1440, quality: 90) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
      }
    `)
  const {
    state: { breakpoint },
  } = useContext(UIContext)
  return (
    <>
      {/* <Spacer height={{ sm: "96px", md: "48px", lg: "96px" }} /> */}
      <Grid noPadding={breakpoint === "sm"}>
        <VariantContainer
          variants={[
            css`
              grid-column: 1/-1;
              position: relative;
            `,
          ]}
        >
          <VariantContainer
            variants={[
              css`
                position: absolute;
                bottom: 0;
                left: 0;
                width: 100%;
                display: grid;
                align-content: center;

                ::before {
                  content: "";
                  position: absolute;
                  top: 0;
                  right: 0;
                  left: 0;
                  bottom: 0;
                  background: var(--tricorn-black);
                  mix-blend-mode: multiply;
                  z-index: 100;
                }
                p {
                  ${T.sm.h2`400`};
                  color: #fff;
                  text-align: center;
                  position: relative;
                  z-index: 200;
                }
              `,
              css`
                ::before {
                  border-radius: 0 0 4px 4px;
                }
              `,
              css`
                left: 50%;
                width: 50%;
                height: 100%;
                ::before {
                  border-radius: 0 4px 4px 0;
                }
              `,
            ]}
          >
            <VariantContainer
              variants={[
                css`
                  padding: 32px 16px;
                  position: relative;
                  color: #fff;
                  z-index: 200;
                  display: grid;
                  grid-template-columns: repeat(12, 1fr);
                `,
                null,
                css`
                  align-self: center;
                  padding: 0 48px;
                  /* The design padding is way too much */
                  @media (min-width: ${Breakpoints.lgPlus}) {
                    padding: 0 112px;
                  }
                `,
              ]}
            >
              <VariantContainer
                variants={[
                  css`
                    ${T.sm.h2`400`};
                    text-align: center;
                    justify-self: center;
                    grid-column: 1/-1;
                  `,
                  css`
                    ${T.md.h2`400`};
                    grid-column: 2/-2;
                  `,
                  css`
                    grid-column: 1/-1;
                    text-align: left;
                    align-self: center;
                    @media (min-width: ${Breakpoints.lgPlus}) {
                      ${T.lg.h3`400`};
                    }
                  `,
                ]}
              >
                Don't see what you're looking for?
              </VariantContainer>
              <VariantContainer
                variants={[
                  css`
                    ${T.sm.body`300`};
                    text-align: center;
                    justify-self: center;
                    padding: 24px 0 32px 0;
                    grid-column: 1/-1;
                  `,
                  css`
                    ${T.body.regular`300`};
                    grid-column: 2/-2;
                  `,
                  css`
                    grid-column: 1/-1;
                    text-align: left;
                    align-self: center;
                    @media (min-width: ${Breakpoints.lgPlus}) {
                      ${T.lg.body`300`};
                    }
                  `,
                ]}
              >
                {description}
              </VariantContainer>
              <Button.Link
                to={buttonHref}
                target="_blank"
                variants={[
                  css`
                    grid-column: 1/-1;
                    justify-self: center;
                  `,
                  css`
                    grid-column: 1/-1;
                  `,
                  css`
                    justify-self: left;
                    align-self: center;
                  `,
                ]}
              >
                {buttonText}
              </Button.Link>
            </VariantContainer>
          </VariantContainer>
          <BreakpointImage
            imgStyle={{ objectFit: "cover", borderRadius: "4px" }}
            imageVariants={{
              lg: desktop.childImageSharp.fluid,
              md: tablet.childImageSharp.fluid,
              sm: mobile.childImageSharp.fluid,
            }}
          />
        </VariantContainer>
      </Grid>
    </>
  )
}
